import PropTypes from 'prop-types';
import React from 'react';
import { browserHistory, Link } from 'react-router';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { DL_SERVICE_URL } from '../../config';
import { downloadFile, gaEvent } from '../../utils/utils';
import * as d3 from 'd3';
import ComponentIcons from '../ComponentIcons';
import Modal from '../Modal';
import DownloadForm from '../DownloadForm';
import ZillowCaption from '../ZillowCaption';
import ZillowController from '../ZillowController';
import ZillowMap from '../ZillowMap';
import ZillowPathway from '../ZillowPathway';
import ZillowRanking from '../ZillowRanking';
import ZillowSettings from '../ZillowSettings';
import styles from './Zillow.css';
import withStyles from '../../decorators/withStyles';
import { abbrevLower, sameLocationNewHash, getDefaultZillowPlaceType, getZillowSS2link, getZillowEndYr, plural, slugify } from "../../utils/utils";
import AnchoredComponent from "../AnchoredComponent";
import { SetModalTitle, SetModalVisible, SetZillowVarType, SetZillowYrBuilt } from "../../actions";


class Zillow extends AnchoredComponent {

  static propTypes = {
    elevMeta: PropTypes.object.isRequired,
    isEmbed: PropTypes.bool.isRequired,
    mapPosition: PropTypes.object.isRequired,
    placeType: PropTypes.string.isRequired,
    placeID: PropTypes.string.isRequired,
    zillowIsPct: PropTypes.bool,
    zillowModel: PropTypes.string.isRequired,
    zillowPathway: PropTypes.string.isRequired,
    zillowPlaceType: PropTypes.string,
    zillowPlaceTypeOptions: PropTypes.array,
    zillowProb: PropTypes.string.isRequired,
    zillowForecastSubtype: PropTypes.string.isRequired,
    zillowVarType: PropTypes.string,
    zillowYear: PropTypes.string,
    zillowYrBuilt: PropTypes.string,
  };

  static defaultProps = {
    zillowIsPct: false,
    zillowVarType: 'Zestimate',
    zillowYear: '2050',
    zillowYrBuilt: "All",
  };

  constructor(props) {
    super(props);
    const defaultPlaceType = getDefaultZillowPlaceType(props.placeType);
    this.state = {
      data: {},
      dlType: 'zillow-img',
      downloadOpen: false,
      settingsOpen: false,
      sourcesOpen: false,
      refs: [],
      defaultZillowPlaceType: defaultPlaceType,
    };
  }

  componentWillReceiveProps(props) {
    const endYr = getZillowEndYr(props.placeID);
    if (endYr === '2005') {
      if (props.zillowYrBuilt !== 'All') {
        this.props.dispatch(SetZillowYrBuilt('All'));
      }
      if (props.zillowVarType === 'Growth') {
        this.props.dispatch(SetZillowVarType('Zestimate'));
      }
    }
  }

  componentDidMount() {
    super.componentDidMount();
  }

  componentDidUpdate(props) {
    const tabs = d3.selectAll('.Zillow .collapsible.active-tab').nodes();
    tabs.forEach(function(tab) {
      const content = tab.nextElementSibling;
      content.style.maxHeight = content.scrollHeight + "px";
    });
  }

  // setTxContent = el => {
  //   d3.select(el).attr("tx-content", "block");
  // };

  getZillowPlaceType() {
    return this.props.zillowPlaceType || this.state.defaultZillowPlaceType;
  }

  openInfo = (e) => {
    gaEvent('modal', 'open', 'info-zillow');
    browserHistory.push({pathname: "/about", hash: "#about-zillow",
      state: {modal: true, modalTitle: "About this site", returnTo: location.pathname}});
  };

  openSettings = (e) => {
    this.closeAll();
    gaEvent('modal', 'open', 'settings-zillow');
    this.props.dispatch(SetModalVisible(true));
    this.setState({settingsOpen: true});
  };

  closeSettings = (e) => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({settingsOpen: false});
  };

  openDownload = (e) => {
    this.closeAll();
    this.props.dispatch(SetModalVisible(true));
    this.setState({downloadOpen: true});
  };

  closeDownload = (e) => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({downloadOpen: false});
  };

  openSources = (e) => {
    e.preventDefault();
    this.closeAll();
    this.props.dispatch(SetModalVisible(true));
    this.setState({sourcesOpen: true});
  };

  closeSources = (e) => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({sourcesOpen: false});
  };

  closeAll = () => {
    this.props.dispatch(SetModalVisible(false));
    this.setState({sourcesOpen: false, downloadOpen: false, settingsOpen: false});
  };

  setDlType(dlType) {
    if (dlType.includes('-pptx')) {
      this.props.dispatch(SetModalTitle('Get slide'));
    } else if (dlType.includes('-img')) {
      this.props.dispatch(SetModalTitle("Get image"));
    } else if (dlType.includes('-xls')) {
      this.props.dispatch(SetModalTitle("Get spreadsheet"));
    }
    this.setState({dlType});
  }

  handleDownloadClick = (dlType) => {
    gaEvent('modal', 'open', `download-${dlType}`);
    this.setDlType(dlType);
    this.openDownload();
  }

  ssTarget(dlType) {
    const { placeType, placeID } = this.props;
    const urlParams = [
      'zillowIsPct',
      'zillowModel',
      'zillowPathway',
      'zillowPlaceType',
      'zillowProb',
      'zillowVarType',
      'zillowYear',
      'zillowYrBuilt',
    ];
    const paramStrings = [];
    for (let paramName of urlParams) {
      const paramValue = this.props[paramName];
      paramStrings.push(`${paramName}=${encodeURIComponent(paramValue)}`);
    }
    const paramStr = paramStrings.join('&');
    const baseURL = 'http://riskfinder.climatecentral.org/embed';
    const lang = Transifex.live.getSelectedLanguageCode();
    return `${baseURL}/${dlType}/${placeType}/${placeID}?${paramStr}&isEmbed=true&lang=${lang}`;
  }

  getFileName = () => {
    const { zillowModel, zillowPlaceTypeOptions } = this.props;
    const zillowPlaceType = this.getZillowPlaceType();
    const modelNames = { DP16: 'Kopp2017', BK: 'Kopp2014' };
    const option = zillowPlaceTypeOptions.filter((k) => k.placeTypeValue === zillowPlaceType)[0];
    const placeLabel = option.placeTypeLabel.replace('major ', '');
    const fileName = slugify(`flood-projections-for-${plural(placeLabel)}-in-${option.containerName}-${modelNames[zillowModel]}`);
    return fileName + '.txt';
  }

  downloadTSV(onDlComplete) {
    const { placeType, placeID, zillowForecastSubtype, zillowModel } = this.props;
    const link = document.createElement('a');
    const zillowPlaceType = this.getZillowPlaceType();
    const fileName = this.getFileName();
    link.href = `/api/housing/table/${placeType}/${placeID}/${zillowForecastSubtype}/${zillowModel}/${zillowPlaceType}/${fileName}`;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    if (onDlComplete) {
      onDlComplete(null, link.href);
    }
  }

  dlCallback = (onDlComplete) => {
    const { placeType, placeID } = this.props;
    const { dlType } = this.state;
    const imgParams = 'width=1201&height=900';
    let url, filename;
    if (dlType === 'zillow-img') {
      filename = `surging-seas-zillow-${placeID}.png`;
      url = DL_SERVICE_URL + "/screenshot" + `?filename=${filename}&${imgParams}&url=` +
        encodeURIComponent(this.ssTarget('housing-img'));
      downloadFile(url, filename, onDlComplete);
    } else if (dlType === 'zillow-pptx') {
      filename = `surging-seas-zillow-${placeID}.pptx`;
      url = DL_SERVICE_URL + '/screenshot' + `?format=pptx&filename=${filename}&${imgParams}&url=` +
        encodeURIComponent(this.ssTarget('housing-img'));
      downloadFile(url, filename, onDlComplete);
    } else if (dlType === 'zillow-xls') {
      this.downloadTSV(onDlComplete);
    }
  };

  render() {
    const { boundaryMeta, elevMeta, data, isEmbed, mapPosition, modalVisible, noData,
            placeName, placeType, placeID, zillowIsPct, zillowModel,
            zillowPathway, zillowPlaceTypeOptions, zillowProb,
            zillowRanking, zillowVarType, zillowYear, zillowYrBuilt } = this.props;
    const zillowPlaceType = this.getZillowPlaceType();
    const zillowEndYr = getZillowEndYr(placeID);
    const ss2Link = getZillowSS2link(mapPosition);

    const downloadModalText = () => {
      if (this.state.dlType === "zillow-xls") {
        return (
          <div>
            <ZillowSettings
              zillowModel={zillowModel}
              zillowPlaceType={zillowPlaceType}
              zillowPlaceTypeOptions={zillowPlaceTypeOptions}
              forDownload={true}
            />
          </div>
        );
      } else {
        return '';
      }
    };

    const extLink = (
      <a href={ss2Link} target="_blank" title="View Risk Zone Map">
        <svg className="icon externallink" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 22 22">
          <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
        </svg>
      </a>
    );

    const componentIcons = (
      <ComponentIcons
        extLink={extLink}
        hasDownload={false}
        openSettings={this.openSettings.bind(this)}
        openInfo={this.openInfo}
      />
    );

    const header = (
      <div className="header">
        <a className="anchor" id="housing"/>
        <Link className="hash-link" to={sameLocationNewHash("#housing")}>
          <h1>Future Flood Risk to Homes<span className="anchor-hash">#</span></h1>
        </Link>
        {componentIcons}
      </div>
    );

    const zillowController = (
      <ZillowController
        isEmbed={isEmbed}
        handleDownloadClick={this.handleDownloadClick.bind(this)}
        zillowEndYr={zillowEndYr}
        zillowIsPct={zillowIsPct}
        zillowModel={zillowModel}
        zillowPathway={zillowPathway}
        zillowPlaceType={zillowPlaceType}
        zillowPlaceTypeOptions={zillowPlaceTypeOptions}
        zillowProb={zillowProb}
        zillowVarType={zillowVarType}
        zillowYrBuilt={zillowYrBuilt}
        zillowYear={zillowYear}
      />
    );

    const pathwayButtons = (
      <ZillowPathway
        handleAdvancedClick={this.openSettings.bind(this, "#select-zillow-scenario")}
        zillowModel={zillowModel}
      />
    );

    const rankings = (
      <ZillowRanking
        placeList={zillowRanking}
        zillowIsPct={zillowIsPct}
        zillowVarType={zillowVarType}
      />
    );

    const logos = (
      <div className="partner-logos">
        <div>Powered by</div>
        <a href="https://www.climatecentral.org/" target="_blank"><img src="/static/CClogo_stacked_color.svg" alt="Climate Central" /></a>
        <a href="https://www.zillow.com/research/" target="_blank"><img src="/static/ZillowLogo_Square.svg" alt="Zillow" /></a>
      </div>
    );

    const choropleth = (
      <div className="ZillowChoropleth">
        <ZillowMap
          placeType={placeType}
          placeID={placeID}
          placeName={placeName}
          noData={noData}
          zillowIsPct={zillowIsPct}
          zillowData={data.zillowData}
          zillowEndYr={zillowEndYr}
          zillowModel={zillowModel}
          zillowPathway={zillowPathway}
          zillowPlaceType={zillowPlaceType}
          zillowVarType={zillowVarType}
          zillowYrBuilt={zillowYrBuilt}
          zillowYear={zillowYear}
        />
        <div className="ZillowChoroplethSidebar">
          {rankings}
          {!isEmbed ? pathwayButtons : ''}
          {logos}
        </div>
      </div>
    );

    const caption = (
      <ZillowCaption
        elevMeta={elevMeta}
        zillowEndYr={zillowEndYr}
        zillowModel={zillowModel}
        zillowPathway={zillowPathway}
        zillowProb={zillowProb}
        zillowVarType={zillowVarType}
      />
    );

    const settingsModal = (
      <Modal isOpen={this.state.settingsOpen && modalVisible} handleClick={this.closeSettings}
             title="Advanced settings">
        <ZillowSettings
          zillowEndYr={zillowEndYr}
          zillowIsPct={zillowIsPct}
          zillowModel={zillowModel}
          zillowPathway={zillowPathway}
          zillowPlaceType={zillowPlaceType}
          zillowPlaceTypeOptions={zillowPlaceTypeOptions}
          zillowProb={zillowProb}
          zillowVarType={zillowVarType}
          zillowYrBuilt={zillowYrBuilt}
          zillowYear={zillowYear}
        />
      </Modal>
    );

    const downloadModal = (
      <Modal isOpen={this.state.downloadOpen && modalVisible} handleClick={this.closeDownload}>
        <DownloadForm
          id="ZillowDLForm"
          placeType={placeType}
          placeID={placeID}
          dlType={this.state.dlType}
          productID="zillow"
          dlCallback={this.dlCallback}
        >
          {downloadModalText()}
        </DownloadForm>
      </Modal>
    );

    return (
      <div className="Zillow">
      {!isEmbed ? header : ''}
        <div className="zillowSection">
          <div className="zillowMapTitle">
            {zillowController}
          </div>
          {choropleth}
          {caption}
        </div>
        {settingsModal}
        {downloadModal}
      </div>
    );
  }
}

// Use connect solely for access to redux "dispatch";
// Don't use any state from the store. Such props will be passed directly.
export default compose(
  withStyles(styles),
  connect(() => {return {};}),
)(Zillow);
